/**
 * Cessna 172S (Skyhawk SP) physical constants and aerodynamic coefficients.
 *
 * Values are representative / plausible for instrument-training purposes rather
 * than certified data. They are tuned so that the resulting flight model shows
 * realistic speeds and responses (cruise ~110-120 KIAS, Vy ~74 KIAS, stall ~48
 * KIAS clean) — see README for the tuning targets.
 *
 * Units: SI (metres, kilograms, seconds, radians, Newtons) unless noted.
 */

export const G = 9.80665; // m/s^2
export const RHO0 = 1.225; // kg/m^3 sea-level ISA density

// --- Mass & geometry -------------------------------------------------------
export const MASS = 1111; // kg (~2450 lb, mid loading; MTOW 1157 kg / 2550 lb)
export const WING_AREA = 16.17; // m^2 (174 ft^2)
export const WING_SPAN = 11.0; // m (36 ft 1 in)
export const MEAN_CHORD = 1.494; // m
export const ASPECT_RATIO = (WING_SPAN * WING_SPAN) / WING_AREA; // ~7.5

// Moments of inertia about body axes (kg*m^2), approximate for a C172-class aircraft
export const IX = 1285; // roll
export const IY = 1825; // pitch
export const IZ = 2667; // yaw

// --- Lift ------------------------------------------------------------------
export const CL0 = 0.31; // lift coefficient at zero angle of attack
export const CL_ALPHA = 5.7; // per radian
export const CL_MAX = 1.55; // clean stall
export const ALPHA_STALL = 0.30; // rad (~17 deg) clean stall angle of attack
// Flap contribution: [CL bonus, extra CL_max, extra drag CD] indexed by detent
export const FLAP_DETENTS = [0, 10, 20, 30]; // degrees, matching the panel
export const FLAP_DCL = [0.0, 0.35, 0.6, 0.9];
export const FLAP_DCL_MAX = [0.0, 0.25, 0.45, 0.6];
export const FLAP_DCD = [0.0, 0.007, 0.02, 0.06];
export const FLAP_PITCH = [0.0, -0.06, -0.10, -0.14]; // pitching-moment bias

// --- Drag ------------------------------------------------------------------
export const CD0 = 0.031; // parasite drag (clean, gear fixed)
export const OSWALD_E = 0.75;
export const K_INDUCED = 1 / (Math.PI * OSWALD_E * ASPECT_RATIO); // ~0.057

// --- Side force / lateral-directional --------------------------------------
export const CY_BETA = -0.31; // side force per rad of sideslip
// CY_BETA/CL_BETA/CN_BETA are small-perturbation linear stability derivatives,
// only valid for modest sideslip. A stationary aircraft sitting in a beam wind
// has beta approaching +-90 deg (near-zero forward airspeed component), where
// the linear model is meaningless and produces runaway roll/yaw moments. Clamp
// the sideslip fed into those three coefficients at this limit.
export const BETA_LIMIT = 0.35; // rad (~20 deg)

// --- Pitch moment ----------------------------------------------------------
export const CM0 = 0.04;
export const CM_ALPHA = -0.89; // static longitudinal stability (per rad)
export const CM_Q = -12.4; // pitch damping
// Control authorities use the sim's command convention: positive elevator/trim
// command => nose-up pitching moment (hence positive, not the textbook sign).
// Sized so that FULL aft elevator reaches ~stall AoA (deliberate stalls remain
// possible), while normal partial input gives a clean climb attitude.
export const CM_DE = 0.6; // elevator effectiveness (per rad of command)
export const CM_TRIM = 0.4; // trim effectiveness (fine, hands-off adjustment)

// --- Roll moment -----------------------------------------------------------
export const CL_BETA = -0.089; // dihedral effect
export const CL_P = -0.47; // roll damping
export const CL_DA = 0.178; // aileron effectiveness

// --- Yaw moment ------------------------------------------------------------
export const CN_BETA = 0.065; // weathercock stability
export const CN_R = -0.099; // yaw damping
export const CN_DR = 0.12; // rudder effectiveness (+ command => yaw right)
export const CN_DA = -0.011; // adverse yaw from aileron

// --- Control surface travel limits (radians) -------------------------------
export const AILERON_MAX = 0.35;
export const ELEVATOR_MAX = 0.42;
export const RUDDER_MAX = 0.28;
export const TRIM_MAX = 0.30;

// --- Engine / propeller (Lycoming IO-360-L2A, ~180 hp) ---------------------
export const IDLE_RPM = 600;
export const MAX_RPM = 2700;
export const MAX_POWER = 134000; // W (~180 hp)
export const PROP_EFFICIENCY = 0.78;
export const STATIC_THRUST = 3000; // N at full throttle, sea level, V=0
export const THRUST_ZERO_SPEED = 92; // m/s where thrust from prop -> ~0

// Fuel
export const FUEL_CAPACITY = 200; // litres usable (~53 US gal)
export const FUEL_BURN_MAX = 0.0165; // litres/sec at full power (~35 L/h)

// --- Landing gear type ------------------------------------------------------
// The C172S has fixed (non-retractable) gear: CD0 above already bakes in its
// drag, and a gear lever has no real effect. These flags exist so a future
// retractable-gear aircraft profile can flip HAS_RETRACTABLE_GEAR and have the
// gear-up/down control actually change drag; see FlightModel's use of it.
export const HAS_RETRACTABLE_GEAR = false;
export const GEAR_DRAG_DELTA = 0.02; // extra CD when a retractable gear is down

// --- Landing gear: real 3-point tricycle geometry ---------------------------
// Each wheel is modeled independently at its own body-frame position (x fwd,
// y right, z down, relative to the CG) with its own spring/damper contact
// force and friction, so roll/pitch/yaw ground behavior (wing-low touchdown
// righting itself, differential braking yawing the aircraft, the nosewheel
// lifting off during rotation, etc.) emerges from the geometry instead of
// being faked with artificial leveling springs. Positions approximate a real
// C172S: ~1.9 m wheelbase, ~2.1 m main gear track.
export const GEAR_HEIGHT = 1.2; // m: CG height above ground, gear extended (also used as a floor-clamp safety net)

export const NOSE_GEAR_X = 1.55; // m forward of CG
export const NOSE_GEAR_Y = 0;
export const NOSE_GEAR_Z = GEAR_HEIGHT;

export const MAIN_GEAR_X = -0.35; // m aft of CG
export const MAIN_GEAR_Y = 1.07; // m either side of centreline (half of ~2.1 m track)
export const MAIN_GEAR_Z = GEAR_HEIGHT;

// Stiffness/damping are per wheel (not per aircraft): mains carry most of the
// static weight so are stiffer; the nose gear carries much less. Chosen so
// static compression settles at a plausible few centimetres per strut.
export const MAIN_GEAR_STIFFNESS = 70000; // N/m, each main wheel
export const MAIN_GEAR_DAMPING = 6000; // N/(m/s), each main wheel
export const NOSE_GEAR_STIFFNESS = 45000; // N/m
export const NOSE_GEAR_DAMPING = 4000; // N/(m/s)

export const ROLLING_FRICTION = 0.03;
export const BRAKE_FRICTION = 0.5; // toe brakes act on the MAIN wheels only — the
// C172's nosewheel is free-castering with no brake, matching the real aircraft.
export const NOSEWHEEL_STEER_COEFF = 0.35; // lateral force per unit rudder command, scaled by nose-wheel normal load — gives real low-speed ground steering instead of relying on (weak, at taxi speed) aerodynamic rudder alone.

// --- Speeds (KCAS/KIAS reference, informational) ---------------------------
export const V_ROTATE_KIAS = 55;
export const V_Y_KIAS = 74;
export const V_APP_KIAS = 65;
export const V_S1_KIAS = 40; // bottom of green arc (clean stall)
export const V_NO_KIAS = 129; // top of green arc (max structural cruising speed)
export const V_NE_KIAS = 163; // red line
export const V_FE_KIAS = 85; // full-flap limit, top of white arc
